/**
 * 自社の広告の枠の日本語の文言（en.selfAds.ts と同じキーを全部持つ = 型で強制）。言い回しの決め事は en.selfAds.ts の冒頭。
 * 日本語は既定のビルドでは出さない（VITE_KUMO_LANGUAGES・2026-10-05）。出すときに読める日本語にしておく。
 */
import type { enSelfAds } from './en.selfAds';
import type { PluralMsg } from './index';

type SelfAdsMessages = { [K in keyof typeof enSelfAds]: (typeof enSelfAds)[K] extends string ? string : string | PluralMsg };

export const jaSelfAds: SelfAdsMessages = {
  'ssa.nav': 'アプリの広告',
  'ssa.title': '自分のアプリの広告',
  'ssa.lead': '公開中の自分のアプリのバナーを、ほかの{product}のアプリの中に出せます。払うのは表示された分だけ。1,000 回で {price} です。',
  'ssa.new': '広告を作る',
  'ssa.noLine': '一文はまだありません',
  'ssa.allStopped': 'いま{product}全体で作り手の広告を止めています。表示も引き落としもありません。再開すると広告も自動で戻ります。',
  'ssa.empty.title': 'アプリに人を呼ぶ',
  'ssa.empty.body': '公開中の自分のアプリを選ぶと、AI がバナーを作ります。1 日の上限を決めれば、ほかの作り手のアプリの下に広告が出ます。払うのは 1,000 回の表示で {price} です。',
  'ssa.off.title': 'まだ使えません',
  'ssa.off.body': 'このサーバーでは、自分のアプリの広告はまだ開いていません。',
  'ssa.row.perDay': '1 日 {amount}・約 {views} 回',
  'ssa.row.spent': '{amount} 使用',
  'ssa.row.continue': '続きを作る',
  'ssa.row.fix': '直してもう一度出す',

  'ssa.status.draft': 'まだ出していません',
  'ssa.status.reviewing': '確認中',
  'ssa.status.active': '配信中',
  'ssa.status.paused': '止めています',
  'ssa.status.rejected': '通りませんでした',
  'ssa.status.stopped': '{product}が止めました',
  'ssa.status.ended': '終わりました',
  'ssa.why.draft': 'この広告はまだ出していません。手順を最後まで進めて、確認に出してください。',
  'ssa.why.reviewing': '広告を確認しています。ふつうは 1 日以内に終わります。配信が始まるまでお金はかかりません。',
  'ssa.why.active': 'ほかの作り手のアプリに広告が出ています。引き落としは 1 日 1 回、表示された分だけです。',
  'ssa.why.paused_advertiser': 'あなたが止めた広告です。再開するまで表示も引き落としもありません。',
  'ssa.why.paused_insufficient_credits': '残高が今日の上限に足りないため止まっています。入金してから再開してください。',
  'ssa.why.paused_target_changed': 'アプリの新しい版を公開したため止まっています。広告をもう一度確認し、終われば自動で配信に戻ります。',
  'ssa.why.paused_advertiser_stopped': '{product}が止めました。間違いだと思うときはサポートに連絡してください。',
  'ssa.why.rejected': 'この広告は通りませんでした。かかったのはバナーを作った AI の費用だけです。直してもう一度出してください。',
  'ssa.why.stopped': '{product}がこの広告を止めました。再開はできません。',
  'ssa.why.ended': '総額の上限に届いたので、この広告は終わりました。',

  'ssa.new.title': '広告を作る',
  'ssa.new.back': '広告の一覧',
  'ssa.step.what': 'アプリ',
  'ssa.step.banner': 'バナー',
  'ssa.step.budget': '予算',
  'ssa.step.review': '出す',
  'ssa.next': '次へ',
  'ssa.back': '戻る',
  'ssa.optional': '（なくても大丈夫）',
  'ssa.saved': '保存しました。',

  'ssa.what.title': 'どのアプリの広告を出しますか？',
  'ssa.what.lead': '公開中で、だれでも遊べる（子ども向けでない）アプリを選べます。広告を押した人はそのアプリへ行きます。',
  'ssa.what.pick': 'あなたのアプリ',
  'ssa.what.noGames': 'まだアプリがありません。先に 1 つ公開してから戻ってきてください。',
  'ssa.what.pasteToggle': 'アプリのアドレスを貼る',
  'ssa.what.pasteLabel': 'アプリのアドレス',
  'ssa.what.check': '確かめる',
  'ssa.what.checking': 'アプリを確かめています…',
  'ssa.what.ok': '公開中で、だれでも遊べます。広告を出せます。',
  'ssa.what.hint': 'AI に伝えたいことは？',
  'ssa.what.hintPlaceholder': '例: 1 分で嵐の海を抜ける',
  'ssa.what.make': '続ける',
  'ssa.what.making': '保存しています…',
  'ssa.what.fixed': 'ほかのアプリの広告は、新しい広告として作ってください。',

  'ssa.banner.title': 'バナー',
  'ssa.banner.lead': 'AI で作るか、自分の画像を使います。スマホ用と PC 用の 2 つの大きさで出ます。',
  'ssa.banner.aiTitle': 'AI で作る',
  'ssa.banner.aiHelp': 'AI があなたのアプリから一文入りのバナーを描きます。AI の費用は残高から引かれます。',
  'ssa.banner.aiMake': 'AI で作る',
  'ssa.banner.aiMakeCost': 'AI で作る（約 {cost}）',
  'ssa.banner.uploadTitle': 'または自分の画像を使う',
  'ssa.banner.uploadHelp': 'PNG・JPEG・WebP。1 枚の画像を 2 つの大きさ（320×50 と 728×90）に合わせて真ん中を切り抜くので、横長の画像が向いています。アップロードには AI の費用はかかりません。',
  'ssa.banner.uploadPick': '画像を選ぶ',
  'ssa.banner.uploading': 'アップロードしています…',
  'ssa.banner.uploaded': '画像を入れました。',
  'ssa.banner.uploadEach': '大きさごとに別の画像を使う',
  'ssa.banner.uploadPhone': 'スマホ用の画像（320×50）',
  'ssa.banner.uploadComputer': 'PC 用の画像（728×90）',
  'ssa.banner.uploadNotImage': 'PNG・JPEG・WebP の画像を選んでください。',
  'ssa.banner.uploadTooBig': '画像が大きすぎます。15 MB より小さい物を選んでください。',
  'ssa.banner.checkNote': '広告はどれも、出る前に AI と運営が確認します。性的なもの・グロテスクなもの・薬物・ギャンブル・差別・詐欺は出せません。',
  'ssa.banner.phone': 'スマホ',
  'ssa.banner.computer': 'PC',
  'ssa.banner.play': '遊ぶ',
  'ssa.banner.loading': '読み込み中…',
  'ssa.banner.sample': '見本',
  'ssa.banner.none': 'バナーはまだありません',
  'ssa.banner.line': '一文',
  'ssa.banner.count': '{n}/{max}',
  'ssa.banner.lineHelp': '短くはっきりがいちばんです。空ならアプリの名前を使います。',
  'ssa.banner.saveLine': '一文を保存',
  'ssa.banner.again': '作り直す',
  'ssa.banner.againCost': '作り直す（約 {cost}）',
  'ssa.banner.making': '作っています…',
  'ssa.banner.use': 'このバナーにする',

  'ssa.budget.title': 'いくら使いますか？',
  'ssa.budget.daily': '1 日の上限',
  'ssa.budget.option': '1 日 {amount}・約 {views} 回',
  'ssa.budget.dailyHelp': 'はじめは {suggested} がおすすめです。日によっては少なく済み、1 日にこれより多くかかることはありません。',
  'ssa.budget.total': '全部での上限',
  'ssa.budget.totalOption': '全部で {amount}・約 {views} 回',
  'ssa.budget.noLimit': '全部での上限なし',
  'ssa.budget.totalHelpNone': '止めるか、残高がなくなるまで続きます。',
  'ssa.budget.totalHelp': 'この額に届いたら自動で終わります。',
  'ssa.budget.price': '値段',
  'ssa.budget.priceValue': '1,000 回の表示で {price}（税込み）',
  'ssa.budget.balance': '広告に使える残高',
  'ssa.budget.lasts': '約 {count} 日分',
  'ssa.budget.which': '広告に使えるのは、カードで入れたお金と、売上から移したお金だけです。招待のクレジットと{product}からのクレジットは使えません。',
  'ssa.budget.whichLaunch': '広告に使えるのは、カードで入れたお金だけです。招待のクレジットと{product}からのクレジットは使えません。',
  'ssa.budget.ownGames': '自分のアプリには自分の広告は出ません。',
  'ssa.budget.charged': '毎日、1 日の上限の分を先に取り分けておき、表示された分だけを引いて、残りは戻します。',
  'ssa.budget.short': '残高が {amount} より少ないので、まだ配信を始められません。',
  'ssa.budget.addFunds': '入金する',

  'ssa.review.title': '確かめて出す',
  'ssa.review.game': 'アプリ',
  'ssa.review.change': '変える',
  'ssa.review.nextTitle': 'このあと',
  'ssa.review.next1': 'すぐに AI が確認し、そのあと運営が確認します。ふつうは 1 日以内です。',
  'ssa.review.next2': '通ったら、ほかの作り手のアプリに出始めます。',
  'ssa.review.next3': '引き落としは 1 日 1 回、表示された分だけ。いつでも止められます。',
  'ssa.review.send': '確認に出す',
  'ssa.review.sending': '送っています…',

  'ssa.detail.all': '広告の一覧',
  'ssa.detail.sent': '送りました。ふつうは 1 日以内に確認が終わります。',
  'ssa.detail.status': '状態',
  'ssa.detail.reason': '理由:',
  'ssa.detail.pause': '止める',
  'ssa.detail.resume': '再開する',
  'ssa.detail.today': '今日',
  'ssa.detail.total': 'これまで',
  'ssa.detail.week': '表示の回数（直近 7 日）',
  'ssa.detail.weekLabel': '直近 7 日で {views} 回の表示',
  'ssa.detail.weekNote': '1 日の区切りは UTC の 0 時です。',
  'ssa.detail.where': '出たアプリ',
  'ssa.detail.whereNone': 'まだどこにも出ていません。',
  'ssa.detail.whereNote': '直近 7 日。1 つのアプリに出るのは表示の 5 分の 1 までです。',
  'ssa.detail.limits': '上限',
  'ssa.detail.changeLimits': '変える',
  'ssa.detail.saveLimits': '上限を保存',
  'ssa.detail.limitsNote': '上げた分はすぐ、下げた分は明日から効きます。',
  'ssa.detail.ad': 'あなたの広告',
  'ssa.detail.goesTo': '行き先',
  'ssa.stat.views': '表示',
  'ssa.stat.clicks': '押された',
  'ssa.stat.rate': '表示の {rate}',
  'ssa.stat.spent': '使った額',
  'ssa.stat.spentTodayHint': 'いまの見込み。日の終わりに引きます。',
  'ssa.stat.charged': 'これまでに引いた額',
  'ssa.stat.chargedHint': '今日の {amount} は日の終わりに足されます。',
  'ssa.stat.leftToday': '今日の残り',
  'ssa.stat.ofDaily': '1 日 {amount} のうち',
  'ssa.stat.leftTotal': '全部での残り',
  'ssa.stat.ofTotal': '{amount} のうち',
  'ssa.stat.noLimit': '上限なし',

  'ssa.reason.gambling': '賭け・お金がもうかる話',
  'ssa.reason.adult': '大人向け・性的なもの',
  'ssa.reason.violence': 'ひどい暴力・怖いもの',
  'ssa.reason.drugs': 'お酒・たばこ・薬',
  'ssa.reason.deceptive': '詐欺・だます見た目（偽のボタン・偽の警告・「当選しました」）',
  'ssa.reason.impersonation': 'ほかの人のキャラ・ロゴ・名前',
  'ssa.reason.politics': '政治・宗教',
  'ssa.reason.hate': '差別・ヘイト',
  'ssa.reason.external_link': '{product}の外へ連れて行く',
  'ssa.reason.other': '広告に向かない内容',
  'ssa.reason.missing_banner': 'バナーがない',

  'ssa.err.invalid_url': 'アプリのアドレスとして読めません。https:// で始まるアドレスを入れてください。',
  'ssa.err.target_not_kumo_game': '広告を出せるのは{product}で公開しているアプリだけです（独自ドメインやほかのサイトは出せません）。',
  'ssa.err.target_not_owned': '広告を出せるのは自分のアプリだけです。',
  'ssa.err.target_not_public': 'このアプリはまだ公開されていません。先に公開してから戻ってきてください。',
  'ssa.err.target_not_general': '子ども向けのアプリは広告を出せません。',
  'ssa.err.target_suspended': 'このアプリは{product}が広告を止めています。',
  'ssa.err.invalid_daily_cap': '1 日の上限は一覧から選んでください。',
  'ssa.err.invalid_total_cap': '全部での上限は、1 日の上限より小さくできません。',
  'ssa.err.banner_asset_invalid': 'このバナーは使えません。作り直してください。',
  'ssa.err.review_rejected': '自動の確認で問題が見つかりました。バナーか一文を直して、もう一度出してください:',
  'ssa.err.insufficient_ad_credits': '広告に使える残高がありません。カードで入金してから、もう一度お試しください。',
  'ssa.err.invalid_state': 'この広告はいまその操作ができません。ページを読み直して、いまの状態を確かめてください。',
  'ssa.err.self_ads_stopped': 'いま{product}全体で作り手の広告を止めています。しばらくしてからお試しください。',
  'ssa.err.campaign_not_found': 'この広告はないか、あなたの広告ではありません。',
  'ssa.err.route_missing': 'このサーバーでは、この部分はまだ準備中です。',
  'ssa.err.suggest_failed': 'AI がバナーを作れませんでした。少し待ってからもう一度お試しください。',
  'ssa.err.review_unavailable': '自動の確認がいま使えません。数分後にもう一度お試しください。',
  'ssa.err.review_limit': '今日は何度も確認に出しています。明日もう一度お試しください。',
  'ssa.err.banner_type_invalid': 'PNG・JPEG・WebP の画像を使ってください。',
  'ssa.err.banner_too_large': 'バナーには大きすぎる画像です。小さい物でお試しください。',
  'ssa.err.banner_size_invalid': 'この大きさのバナーに合わない形の画像です。',
  'ssa.err.banner_animated': '動く画像は使えません。止まった画像を使ってください。',

  'ssa.host.legend': 'ここにどの広告を出しますか？',
  'ssa.host.kumo_and_adsterra': '{product}の作り手の広告、なければ Adsterra',
  'ssa.host.kumo_and_adsterra.help': '作り手の広告の方がふつうは多く入ります。ないときは Adsterra が出ます。',
  'ssa.host.kumo_only': '{product}の作り手の広告だけ',
  'ssa.host.kumo_only.help': 'ないときは何も出ません。',
  'ssa.host.adsterra_only': 'Adsterra だけ',
  'ssa.host.adsterra_only.help': 'ほかの作り手の広告は、あなたのアプリに出ません。',
  'ssa.host.recommended': 'おすすめ',
  'ssa.host.note': 'あなた自身の広告はここに出ません。出るのはだれでも向けのアプリの広告だけです。あなたのアプリで出た表示の代金は 100% あなたに入ります。',
  'ssa.host.newTitle': '新しく: ほかの{product}の作り手の広告',
  'ssa.host.newBody': 'ほかの作り手が、あなたのバナーの枠に自分のアプリの広告を出せるようになりました。表示ごとの代金はあなたに入ります。下で「Adsterra だけ」に変えられます。',
  'ssa.host.gotIt': 'わかりました',

  // 旗 house の「KUMO の広告を出す / 出さない」（2026-10-24 house-optout-fill）
  'ssa.house.legend': '{product}の広告',
  'ssa.house.note': 'いまあなたのアプリに出る広告は、{product}の短い宣伝だけです。遊ぶ人が見た分だけあなたに入ります。どこに出すかはあなたが選べます。',
  'ssa.house.banner': 'バナーの枠に{product}の広告を出す',
  'ssa.house.banner.help': '{product}の小さなバナーです。オフにすると、バナーの枠には何も出ません。',
  'ssa.house.rewarded': '報酬のときに{product}の動画を出す',
  'ssa.house.rewarded.help': '遊ぶ人が{product}の短い動画を最後まで見ると、あなたの報酬がもらえます。オフにすると、アプリには「いまは動画がない」と伝わり、報酬は出ません。',

  'ssa.staff.eyebrow': '運営',
  'ssa.staff.title': '確認待ちの広告',
  'ssa.staff.count': '{count} 件',
  'ssa.staff.key': '運営の鍵',
  'ssa.staff.keyHelp': 'このタブの中にだけ置きます。閉じると消えます。',
  'ssa.staff.open': '開く',
  'ssa.staff.reload': '読み直す',
  'ssa.staff.none': 'いま確認する広告はありません。',
  'ssa.staff.goesTo': '行き先',
  'ssa.staff.age': '公開から {count} 日',
  'ssa.staff.ai': '自動の確認',
  'ssa.staff.rules': '決まり',
  'ssa.staff.aiCheck': 'AI',
  'ssa.staff.aiOff': 'このサーバーでは AI の確認を使っていません',
  'ssa.staff.verdict.ok': 'OK',
  'ssa.staff.verdict.ng': 'だめ',
  'ssa.staff.verdict.review': '迷い',
  'ssa.staff.advertiser': '作り手',
  'ssa.staff.topups': 'カードの入金 {count} 回',
  'ssa.staff.budget': '1 日の上限',
  'ssa.staff.sent': '送った日',
  'ssa.staff.approve': 'OK',
  'ssa.staff.reject': 'だめ',
  'ssa.staff.why': '理由（作り手に見えます）',
  'ssa.staff.note': 'メモ',
  'ssa.staff.noteHelp': '（運営だけ）',
  'ssa.staff.rejectConfirm': 'だめにする'
};
